import { CheckCircleOutline, ExpandMore, Paid } from '@mui/icons-material'
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  TextField,
  Box,
  MenuItem,
  InputAdornment,
  Typography,
  colors,
  FormControlLabel,
  Checkbox,
  Button,
} from '@mui/material'
import React, { useEffect, useState } from 'react'
import useAlert from '../../../hooks/useAlert'
import ProductsDetail from '../../components/deal/ProductsDetail'
import AccordionHeader from '../AccordionHeader'
import CloseDealModal from './CloseDealModal'
import AccountOwnerSelect from '../AccountOwnerSelect'
import {
  setPrimaryAccountOwnerUtil,
  setSecondaryAccountOwnersUtil,
} from '../../../utils/accountOwner'
import { addTimezoneOffsetToTimestamp } from '../../../utils/date'
import DeleteConfirmModal from '../DeleteConfirmModal'
import { grey } from '@mui/material/colors'
import { authedAxios } from '../../../utils'
import Tooltip from '../../../common/Tooltip'

const statusLabels = {
  1: 'Open',
  2: 'Won',
  3: 'Lost',
}

const adornmentProps = {
  startAdornment: <InputAdornment position='start'>$</InputAdornment>,
}

const tooltipText =
  'In this section you can track the status of a prospective opportunity that has not closed. Note: While you can include extra products and services to the existing opportunity, it is not possible to have more than one ongoing opportunity simultaneously.'

const CustomerOpportunity = (props) => {
  const {
    isNewOpportunity,
    isNewCustomer,
    onCancel,
    onSave,
    onDelete,
    currentOpportunity,
    setCurrentOpportunity,
    saveOpportunity,
    resetOpportunity,
    closeOpportunity,
    pipelines,
    products,
    billingCycleTypes,
    dropdowns,
    getCustomer,
    setIsDirty,
    accountOwners,
    canSave = false,
    isPrimaryInDealsPage,
    updateProductOfInterest,
    customer,
    expandedAccordions,
    setExpandedAccordions,
    renderedSaveButton,
  } = props

  const { createAlert, createGenericErrorAlert } = useAlert()

  const getLocalStorageItem = () => {
    return JSON.parse(localStorage.getItem('minimizedOpportunities') || '[]')
  }

  const isMinimized = getLocalStorageItem().includes(currentOpportunity?.id)

  const expanded = expandedAccordions
    ? expandedAccordions.opportunity && !isMinimized
    : !isMinimized

  const setExpanded = (val) =>
    setExpandedAccordions((prev) => ({ ...prev, opportunity: val }))

  const [isCanceling, setIsCanceling] = useState(false)
  const [cancelDate, setCancelDate] = useState(null)
  const [cancelReason, setCancelReason] = useState(null)

  const [isEditing, setIsEditing] = useState(
    !isPrimaryInDealsPage && currentOpportunity?.isOpen
  )

  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    if (isNewOpportunity) {
      // setExpanded(true)
      // setIsEditing(true)
    }
  }, [isNewOpportunity])

  const [isClosingDeal, setIsClosingDeal] = useState(false)

  const updateOpportunity = (key, value, skipIsDirty = false) => {
    if (setIsDirty && !skipIsDirty) setIsDirty(true)
    setCurrentOpportunity({
      ...currentOpportunity,
      [key]: value,
    })
  }

  const batchUpdateOpportunity = (obj, skipIsDirty = false) => {
    if (setIsDirty && !skipIsDirty) setIsDirty(true)
    setCurrentOpportunity({
      ...currentOpportunity,
      ...obj,
    })
  }

  useEffect(() => {
    if (currentOpportunity?.isOpen && isPrimaryInDealsPage) setExpanded(true)
    if (currentOpportunity?.isOpen && !isPrimaryInDealsPage) setIsEditing(true)
  }, [currentOpportunity])

  if (!currentOpportunity) return null

  const stages =
    pipelines.find((p) => p.id === '__OPPORTUNITIES__')?.stages || []

  const updateProducts = (value) => {
    updateOpportunity('products', value)

    const productOfInterest = value.reduce((acc, item) => {
      if (!item.productId) return acc

      const name = products.find((p) => p.id === item.productId)?.name

      return acc ? acc + ', ' + name : name
    }, '')

    updateProductOfInterest?.(productOfInterest)
  }

  const handleCancel = () => {
    onCancel?.()
  }

  const handleSave = () => {
    onSave()
      .then(() => {
        createAlert({ message: 'Opportunity successfully saved' })
        if (canSave) setIsEditing(false)
      })
      .catch(() => createGenericErrorAlert())
  }

  const checkSaveDisabled = () => {
    if (!isEditing || currentOpportunity.isOpen) return false

    const hasAllDates = currentOpportunity?.products?.every((p) => {
      return !!p.startDate
    })

    return !hasAllDates
  }

  const saveDisabled = checkSaveDisabled()

  const cancelDeal = () => {
    authedAxios
      .put(
        `/api/customer/${customer.id}/opportunity/${currentOpportunity.id}/cancel`,
        { cancelDate, cancelReason }
      )
      .then(() => {
        setIsCanceling(false)
        setCancelDate(null)

        const newProducts = (currentOpportunity.products || []).map((p) => {
          if (!p.endDate) return p

          if (new Date(p.endDate) > new Date(cancelDeal)) {
            return { ...p, endDate: cancelDate }
          }

          return p
        })

        batchUpdateOpportunity({
          canceledDate: cancelDate,
          canceledReaso: cancelReason,
          products: newProducts,
        })

        createAlert({ message: 'Successfully canceled an opportunity' })
      })
      .catch((e) => {
        setIsCanceling(false)
        setTimeout(() => {
          createGenericErrorAlert()
        }, 100)
      })
  }

  const handleKeepMinimized = () => {
    const minimized = getLocalStorageItem()

    setExpanded(false)

    localStorage.setItem(
      'minimizedOpportunities',
      JSON.stringify(minimized.concat([currentOpportunity.id]))
    )
  }

  const handleAccordionExpand = (_, isExpanded) => {
    if (isExpanded) {
      const minimized = getLocalStorageItem()

      localStorage.setItem(
        'minimizedOpportunities',
        JSON.stringify(minimized.filter((m) => m !== currentOpportunity.id))
      )
    }

    setExpanded(isExpanded)
  }

  return (
    <>
      {isDeleting && (
        <DeleteConfirmModal
          isOpen={isDeleting}
          onClose={() => setIsDeleting(false)}
          onSubmit={() =>
            onDelete()
              .then(() => {
                createAlert({
                  message: 'Opportunity deleted successfully',
                })
              })
              .catch(() => createGenericErrorAlert())
          }
          deleteString={'this opportunity'}
        />
      )}

      {isClosingDeal && (
        <CloseDealModal
          billingCycleTypes={billingCycleTypes}
          opportunity={currentOpportunity}
          isOpen={isClosingDeal}
          productTypes={products || []}
          onCancel={() => {
            resetOpportunity()
            setIsClosingDeal(false)
          }}
          onSave={(...args) => {
            saveOpportunity()
              .then((res) => {
                closeOpportunity(...args)
                return res
              })
              .then((res) => {
                setCurrentOpportunity(res.data.customerOpportunity)
              })

              .then(() => {
                setIsClosingDeal(false)
                createAlert({ message: 'Successfully closed deal' })
              })
              .catch((e) => {
                setIsClosingDeal(false)
                createGenericErrorAlert()
              })
              .finally(() => {
                if (getCustomer) getCustomer(false)
              })
          }}
          updateProducts={updateProducts}
          dropdowns={dropdowns}
        />
      )}

      <Accordion
        elevation={8}
        sx={{ width: '100%', minWidth: '340px', maxWidth: '1250px' }}
        expanded={expanded || isNewOpportunity}
        onChange={handleAccordionExpand}
      >
        <AccordionSummary expandIcon={<ExpandMore />}>
          <Box
            sx={{
              minWidth: '300px',
            }}
          >
            <Box
              sx={{
                minWidth: '300px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexGrow:
                  isNewOpportunity || currentOpportunity?.isOpen ? 1 : null,
              }}
            >
              <Typography
                sx={{
                  fontSize: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                }}
              >
                <b>
                  {isNewOpportunity && 'New Opportunity'}
                  {!isNewOpportunity &&
                    currentOpportunity?.isOpen &&
                    'Current Opportunity'}
                </b>
                {(isNewOpportunity || currentOpportunity?.isOpen) && (
                  <Paid sx={{ ml: 1 }} color={'primary'} />
                )}

                {!expanded && <Tooltip infoText={tooltipText} sx={{ ml: 1 }} />}

                {currentOpportunity?.isOpen && currentOpportunity?.createdOn && (
                  <Typography
                    variant='span'
                    sx={{ fontSize: '14px', color: colors.grey['700'], ml: 1 }}
                  >
                    <i>
                      Start date{' '}
                      {new Date(
                        currentOpportunity.createdOn
                      ).toLocaleDateString()}
                    </i>
                  </Typography>
                )}
              </Typography>

              {expanded &&
                (isNewOpportunity ||
                  (currentOpportunity?.isOpen ? (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation()
                        handleKeepMinimized()
                      }}
                    >
                      Keep minmimized
                    </Button>
                  ) : (
                    <Box />
                  )))}
            </Box>

            {!isNewOpportunity && !currentOpportunity?.isOpen && (
              <Box>
                {currentOpportunity.canceledDate && (
                  <Box sx={{ mb: 1 }}>
                    <Typography color={'error'}>
                      Canceled on{' '}
                      {new Date(
                        currentOpportunity.canceledDate
                      ).toLocaleDateString()}{' '}
                      by {currentOpportunity.canceledByName}
                    </Typography>
                    <Typography color={'error'}>
                      {expanded &&
                        `Reason: ${currentOpportunity.canceledReason}`}
                    </Typography>
                  </Box>
                )}
                <Typography
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    color: currentOpportunity.canceledDate
                      ? colors.grey['700']
                      : null,
                  }}
                >
                  Closed ({statusLabels[currentOpportunity.statusTypeId]}
                  {currentOpportunity.outcomeReasonName &&
                    ` - ${currentOpportunity.outcomeReasonName}`}
                  )
                  {currentOpportunity.statusTypeId === 2 && (
                    <Paid sx={{ ml: 1 }} color={'primary'} />
                  )}
                </Typography>
                <Typography
                  sx={{ fontSize: '14px', color: colors.grey['700'], mt: 0.5 }}
                >
                  <i>
                    Open date{' '}
                    {addTimezoneOffsetToTimestamp(
                      new Date(currentOpportunity.createdOn)
                    ).toLocaleDateString()}
                  </i>
                </Typography>

                <Typography
                  sx={{ fontSize: '14px', color: colors.grey['700'], mt: 0.5 }}
                >
                  <i>
                    Closed on{' '}
                    {addTimezoneOffsetToTimestamp(
                      new Date(currentOpportunity.closedOn)
                    ).toLocaleDateString()}
                  </i>
                </Typography>
              </Box>
            )}

            {!expanded && (
              <Box>
                <Box sx={{ display: 'flex', mt: 2, flexWrap: 'wrap', gap: 1 }}>
                  {(currentOpportunity.daysToClose ||
                    currentOpportunity.daysToClose === 0) && (
                    <Box sx={{ mr: 4 }}>
                      <>
                        <Typography sx={{ color: grey[700], fontSize: '14px' }}>
                          <b>Days to close</b>
                        </Typography>
                        <Typography>
                          {currentOpportunity.daysToClose} days
                        </Typography>
                      </>
                    </Box>
                  )}
                  {(currentOpportunity.percentToClose ||
                    currentOpportunity.percentToClose === 0) && (
                    <Box sx={{ mr: 4 }}>
                      <>
                        <Typography sx={{ color: grey[700], fontSize: '14px' }}>
                          <b>Percent to close</b>{' '}
                        </Typography>
                        <Typography>
                          {currentOpportunity.percentToClose}%
                        </Typography>
                      </>
                    </Box>
                  )}

                  <RevenueSummary
                    currentOpportunity={currentOpportunity}
                    billingCycleTypes={billingCycleTypes}
                    products={products}
                    collapse
                  />
                </Box>
              </Box>
            )}
          </Box>
        </AccordionSummary>

        <AccordionDetails>
          <AccordionHeader
            rightButtons={[]}
            additionalButtons={
              isNewOpportunity || isNewCustomer || !currentOpportunity?.isOpen
                ? isEditing || currentOpportunity.canceledDate
                  ? []
                  : [
                      {
                        label: 'Cancel deal',
                        onClick: () => {
                          setIsCanceling(true)
                        },
                        variant: 'outlined',
                      },
                    ]
                : [
                    {
                      label: 'Delete opportunity',
                      onClick: () => {
                        setIsDeleting(true)
                      },
                      variant: 'outlined',
                      color: 'error',
                    },

                    {
                      label: 'Close deal',
                      onClick: () => setIsClosingDeal(true),
                      variant: 'contained',
                      endIcon: <CheckCircleOutline />,
                    },
                  ]
            }
            infoText={currentOpportunity?.isOpen ? tooltipText : null}
            isEditing={isEditing}
            setIsEditing={setIsEditing}
            onCancel={handleCancel}
            onSave={handleSave}
            hide={isNewCustomer}
            canSave={canSave}
            hideEdit={!canSave}
            hideRightButtons={
              isNewOpportunity ||
              isNewCustomer ||
              !currentOpportunity?.isOpen ||
              (isEditing && canSave)
            }
            saveDisabled={!isEditing || saveDisabled}
          />

          <Form
            isEditing={isEditing}
            currentOpportunity={currentOpportunity}
            updateOpportunity={updateOpportunity}
            billingCycleTypes={billingCycleTypes}
            stages={stages}
            products={products}
            updateProducts={updateProducts}
            accountOwners={accountOwners}
            isOpen={currentOpportunity.isOpen}
          />
          <Box
            sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2, mr: -2 }}
          >
            {renderedSaveButton && renderedSaveButton}
          </Box>
        </AccordionDetails>
      </Accordion>

      {isCanceling && (
        <DeleteConfirmModal
          isOpen={isCanceling}
          titleOverride={'Cancelling Deal'}
          buttonString={'Cancel Deal'}
          cancelString={'Never mind'}
          onClose={() => setIsCanceling(false)}
          onSubmit={() => cancelDeal()}
          buttonDisabled={!cancelDate || !cancelReason}
        >
          <Box sx={{ width: '300px', py: 2 }}>
            <TextField
              value={cancelDate}
              onChange={(e) => setCancelDate(e.target.value)}
              type='date'
              fullWidth
              label='Canceled date'
              InputLabelProps={{
                shrink: true,
              }}
              margin='dense'
            ></TextField>
            <TextField
              value={cancelReason}
              onChange={(e) => setCancelReason(e.target.value)}
              fullWidth
              label='Reason'
              margin='dense'
            ></TextField>
          </Box>
        </DeleteConfirmModal>
      )}
    </>
  )
}

const Form = ({
  isEditing,
  currentOpportunity,
  updateOpportunity,
  billingCycleTypes,
  stages,
  products,
  updateProducts,
  accountOwners = [],
  isOpen,
}) => {
  const defaultStage = stages.find((s) => s.displayOrder === 1) || null

  const setPrimaryAccountOwner = (identity) => {
    const accountOwners = setPrimaryAccountOwnerUtil(
      identity,
      currentOpportunity.accountOwners || []
    )
    updateOpportunity('accountOwners', accountOwners)
  }
  const setSecondaryAccountOwners = (newVal) => {
    const accountOwners = setSecondaryAccountOwnersUtil(
      newVal,
      currentOpportunity.accountOwners || []
    )
    updateOpportunity('accountOwners', accountOwners)
  }

  return (
    <>
      <Box sx={{ display: 'flex' }}>
        {isOpen && (
          <>
            <TextField
              disabled={!isEditing}
              margin='normal'
              fullWidth
              sx={{ mr: 1 }}
              label={'Days to Close'}
              value={currentOpportunity.daysToClose}
              onChange={(e) => updateOpportunity('daysToClose', e.target.value)}
              select
              // InputProps={{
              //   endAdornment: (
              //     <InputAdornment position='end' sx={{ mr: 2 }}>
              //       {'days'}
              //     </InputAdornment>
              //   ),
              // }}
            >
              {['30', '60', '90+'].map((d, i) => (
                <MenuItem key={i} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              disabled={!isEditing}
              margin='normal'
              fullWidth
              sx={{ mr: 1 }}
              type='number'
              label={'Percent to Close'}
              value={currentOpportunity.percentToClose}
              onChange={(e) => {
                let value = e.target.value

                if (value) {
                  value = Math.min(Number(e.target.value), 100)
                }

                updateOpportunity('percentToClose', value)
              }}
              inputProps={{
                step: 10,
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position='end'>{'%'}</InputAdornment>
                ),
              }}
            ></TextField>
          </>
        )}

        <TextField
          disabled={!isEditing}
          margin='normal'
          fullWidth
          label={'Pipeline Stage'}
          value={currentOpportunity.stageId || defaultStage?.id || 1}
          select
          onChange={(e) =>
            updateOpportunity(
              'stageId',
              e.target.value ? Number(e.target.value) : e.target.value
            )
          }
        >
          {stages.map((s, i) => (
            <MenuItem key={i} value={s.id}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ display: 'flex', mr: -1, flexWrap: 'wrap' }}>
        <AccountOwnerSelect
          disabled={!isEditing}
          primaryAccountOwner={
            currentOpportunity.accountOwners?.find((ao) => ao.isPrimary) ||
            accountOwners.find((ao) => ao.isPrimary) ||
            null
          }
          setPrimaryAccountOwner={setPrimaryAccountOwner}
          secondaryAccountOwners={
            currentOpportunity.accountOwners?.filter((ao) => !ao.isPrimary) ||
            accountOwners.filter((ao) => !ao.isPrimary) ||
            []
          }
          setSecondaryAccountOwners={setSecondaryAccountOwners}
        />
      </Box>
      {isOpen && (
        <Box>
          <FormControlLabel
            required
            control={
              <Checkbox
                onChange={(e) =>
                  updateOpportunity('isInLimbo', e.target.checked)
                }
                checked={currentOpportunity.isInLimbo}
              />
            }
            label='In Limbo'
          />
        </Box>
      )}
      <ProductsDetail
        disabled={!isEditing}
        includeDates={!isOpen}
        productTypes={
          products
            ? products.map((p) =>
                p.isArchived ? { ...p, name: `${p.name} (archived)` } : p
              )
            : []
        }
        updateProducts={updateProducts}
        products={currentOpportunity?.products || []}
        billingCycleTypes={billingCycleTypes}
        currentOpportunity={currentOpportunity}
      />
      <Box sx={{ display: 'flex', mt: 2, flexWrap: 'wrap', gap: 1 }}>
        <RevenueSummary
          currentOpportunity={currentOpportunity}
          billingCycleTypes={billingCycleTypes}
          products={products}
        />
      </Box>
    </>
  )
}

const RevenueSummary = ({
  currentOpportunity,
  billingCycleTypes,
  products,
  collapse,
}) => {
  const isMixedBillingCycle =
    currentOpportunity?.products?.length !== 1 &&
    !currentOpportunity?.products?.every(
      (val, i, arr) => val.billingCycleTypeId === arr?.[0].billingCycleTypeId
    )

  const nonMixedBillingCycleType = billingCycleTypes.find(
    (bct) => bct.id === currentOpportunity?.products?.[0]?.billingCycleTypeId
  )

  const { oneTimeTotal, recurringTotal } = currentOpportunity?.products?.reduce(
    (acc, item) => {
      const productType = products.find((p) => p.id === item.productId)

      if (!productType) return acc

      const price = item.price || productType?.price
      const quantity = item.quantity || 1

      if (item.billingCycleTypeId === 1 || !item.billingCycleTypeId)
        return {
          ...acc,
          oneTimeTotal: acc.oneTimeTotal + price * quantity,
        }

      const billingCycleType = billingCycleTypes.find(
        (bct) => bct.id === item.billingCycleTypeId
      )

      const adjuster = isMixedBillingCycle
        ? 1
        : nonMixedBillingCycleType.ratioToMonth

      return {
        ...acc,
        recurringTotal:
          acc.recurringTotal +
          (price * quantity) / (billingCycleType.ratioToMonth / adjuster),
      }
    },
    { oneTimeTotal: 0, recurringTotal: 0 }
  ) || { oneTimeTotal: 0, recurringTotal: 0 }

  const billingCycleType =
    currentOpportunity?.products?.length === 0
      ? 'None'
      : isMixedBillingCycle
      ? 'Mixed'
      : billingCycleTypes.find(
          (bct) =>
            bct.id === currentOpportunity?.products?.[0].billingCycleTypeId
        )?.name || 'None'

  const isClosed = currentOpportunity?.isOpen

  return (
    <>
      <Box sx={{ mr: collapse ? 4 : 1 }}>
        <Typography sx={{ color: grey[700], fontSize: '14px' }}>
          <b>{isClosed ? `Potential ` : ''}One Time Revenue</b>
        </Typography>
        <Typography>${oneTimeTotal.toFixed(2)}</Typography>
      </Box>
      <Box sx={{ mr: collapse ? 4 : 1 }}>
        <Typography sx={{ color: grey[700], fontSize: '14px' }}>
          <b>
            {isClosed ? `Potential ` : ''}Recurring Revenue{' '}
            {isMixedBillingCycle && '(monthly estimate)'}
          </b>
        </Typography>
        <Typography>${recurringTotal.toFixed(2)}</Typography>
      </Box>
      <Box sx={{ mr: collapse ? 4 : 1 }}>
        <Typography sx={{ color: grey[700], fontSize: '14px' }}>
          <b>Total Revenue</b>
        </Typography>
        <Typography>${(oneTimeTotal + recurringTotal).toFixed(2)}</Typography>
      </Box>
      <Box sx={{ mr: collapse ? 4 : 1 }}>
        <Typography sx={{ color: grey[700], fontSize: '14px' }}>
          <b>Billing Cycle Type</b>
        </Typography>
        <Typography>{billingCycleType}</Typography>
      </Box>
    </>
  )
}

export { Form as CustomerOpportunityForm }

export default CustomerOpportunity
